import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { navigate } from '@/app/navigation';

interface Partner {
  id: string;
  name: string;
  email: string;
  code: string;
  rate6m: string;
  rateNext6m: string;
  createdAt: string;
  referrals: { id: string; status: string }[];
}

export function PartnersPage() {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });

  const { data: partners = [], isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: () => api.get<Partner[]>('/api/partners'),
  });

  const createMutation = useMutation({
    mutationFn: (data: { name: string; email: string }) =>
      api.post<Partner>('/api/partners', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['partners'] });
      setShowForm(false);
      setForm({ name: '', email: '' });
    },
  });

  const activeCount = (p: Partner) => p.referrals.filter((r) => r.status === 'contracted').length;
  const totalCount = (p: Partner) => p.referrals.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Partners</h1>
          <p className="text-muted-foreground">Manage ambassadors and their referrals</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>Add Partner</Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>New Partner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              />
              <Input
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
              <Button
                onClick={() => createMutation.mutate(form)}
                disabled={!form.name || !form.email || createMutation.isPending}
              >
                {createMutation.isPending ? 'Creating...' : 'Create'}
              </Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
            {createMutation.isError && (
              <p className="text-destructive text-sm mt-2">
                {(createMutation.error as Error).message}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : partners.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No partners yet. Add your first ambassador.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {partners.map((p) => (
            <Card
              key={p.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/partners/${p.id}`)}
            >
              <CardContent className="py-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{p.name}</p>
                  <p className="text-sm text-muted-foreground">{p.email}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Code: <code className="bg-muted px-1 rounded">{p.code}</code>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{activeCount(p)} active clients</p>
                  <p className="text-xs text-muted-foreground">{totalCount(p)} total leads</p>
                  <p className="text-xs text-muted-foreground">
                    {Math.round(parseFloat(p.rate6m) * 100)}% / {Math.round(parseFloat(p.rateNext6m) * 100)}%
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
